import {
  Badge,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { RHFTextField } from '../../../../components/hook-form';
import { useFormContext } from 'react-hook-form';
import { useEffect, useState } from 'react';
import AddCanvas from './AddCanvas';
import { IExaminationSchema } from '../../../../@types/examination';

type Props = {
  clinicalRecordId: number;
  setExamination: (data: IExaminationSchema) => void;
};
const Ophthalmoscopy = ({ clinicalRecordId, setExamination }: Props) => {
  const [openCanvasSide, setOpenCanvasSide] = useState<string>('');
  const { setValue, getValues, watch } = useFormContext();

  const leftVisualImage = watch('ophthalmoscopy.leftVisualImage') as
    string | undefined;
  const rightVisualImage = watch('ophthalmoscopy.rightVisualImage') as
    string | undefined;

  const hasLeftFundusImage = Boolean(
    typeof leftVisualImage === 'string' && leftVisualImage.trim(),
  );
  const hasRightFundusImage = Boolean(
    typeof rightVisualImage === 'string' && rightVisualImage.trim(),
  );
  const [leftCanvas, setLeftCanvas] = useState('');
  const [rightCanvas, setRightCanvas] = useState('');

  const clearExternalData = () => {
    const fieldNames = [
      'ophthalmoscopy.leftLens',
      'ophthalmoscopy.leftVitreous',
      'ophthalmoscopy.leftDiscColor',
      'ophthalmoscopy.leftCDRatio',
      'ophthalmoscopy.leftDiscMargins',
      'ophthalmoscopy.leftAVRatio',
      'ophthalmoscopy.leftMacula',
      'ophthalmoscopy.leftPeriphery',
      'ophthalmoscopy.leftFundusApp',
      'ophthalmoscopy.leftMedia',
      'ophthalmoscopy.leftAdditionalNotes',
      'ophthalmoscopy.rightLens',
      'ophthalmoscopy.rightVitreous',
      'ophthalmoscopy.rightDiscColor',
      'ophthalmoscopy.rightCDRatio',
      'ophthalmoscopy.rightDiscMargins',
      'ophthalmoscopy.rightAVRatio',
      'ophthalmoscopy.rightMacula',
      'ophthalmoscopy.rightPeriphery',
      'ophthalmoscopy.rightFundusApp',
      'ophthalmoscopy.rightMedia',
      'ophthalmoscopy.rightAdditionalNotes',
    ];

    const clearedValues = { ...getValues() };
    fieldNames.forEach((fieldName) => {
      clearedValues[fieldName] = '';
    });

    Object.keys(clearedValues).forEach((fieldName) => {
      setValue(fieldName, clearedValues[fieldName]);
    });
  };

  const copyData = () => {
    const leftLens = getValues('ophthalmoscopy.leftLens');
    const leftVitreous = getValues('ophthalmoscopy.leftVitreous');
    const leftDiscColor = getValues('ophthalmoscopy.leftDiscColor');
    const leftCDRatio = getValues('ophthalmoscopy.leftCDRatio');
    const leftDiscMargins = getValues('ophthalmoscopy.leftDiscMargins');
    const leftAVRatio = getValues('ophthalmoscopy.leftAVRatio');
    const leftMacula = getValues('ophthalmoscopy.leftMacula');
    const leftPeriphery = getValues('ophthalmoscopy.leftPeriphery');
    const leftFundusApp = getValues('ophthalmoscopy.leftFundusApp');
    const leftMedia = getValues('ophthalmoscopy.leftMedia');
    const leftAdditionalNotes = getValues('ophthalmoscopy.leftAdditionalNotes');

    setValue('ophthalmoscopy.rightLens', leftLens);
    setValue('ophthalmoscopy.rightVitreous', leftVitreous);
    setValue('ophthalmoscopy.rightDiscColor', leftDiscColor);
    setValue('ophthalmoscopy.rightCDRatio', leftCDRatio);
    setValue('ophthalmoscopy.rightDiscMargins', leftDiscMargins);
    setValue('ophthalmoscopy.rightAVRatio', leftAVRatio);
    setValue('ophthalmoscopy.rightMacula', leftMacula);
    setValue('ophthalmoscopy.rightPeriphery', leftPeriphery);
    setValue('ophthalmoscopy.rightFundusApp', leftFundusApp);
    setValue('ophthalmoscopy.rightMedia', leftMedia);
    setValue('ophthalmoscopy.rightAdditionalNotes', leftAdditionalNotes);
  };

  const handleCanvasModal = (side: string) => {
    setOpenCanvasSide(side);
  };

  const handleCloseCanvasModal = () => {
    setOpenCanvasSide('');
  };

  // A mount-only effect used to snapshot the two image URLs into local state.
  // It ran before the parent had reset the form with the loaded examination, so
  // it stored `undefined` and the popup opened blank for a record that did have
  // an image (client bug 32). The watched form values are read directly instead.

  useEffect(() => {
    if (rightCanvas) {
      setValue('ophthalmoscopy.rightVisualImage', rightCanvas);
    }
    if (leftCanvas) {
      setValue('ophthalmoscopy.leftVisualImage', leftCanvas);
    }
    // canvasResult({
    //   rightVisualImage: rightCanvas === "" ? null : rightCanvas,
    //   leftVisualImage: leftCanvas === "" ? null : leftCanvas,
    // });
  }, [rightCanvas, leftCanvas]);

  const defaultValues = () => {
    setValue('ophthalmoscopy.leftLens', 'Clear');
    setValue('ophthalmoscopy.leftVitreous', 'Clear');
    setValue('ophthalmoscopy.leftDiscColor', 'Pink');
    setValue('ophthalmoscopy.leftCDRatio', '');
    setValue('ophthalmoscopy.leftDiscMargins', 'Healthy');
    setValue('ophthalmoscopy.leftAVRatio', '2/3');
    setValue('ophthalmoscopy.leftMacula', 'Clear');
    setValue('ophthalmoscopy.leftPeriphery', 'Clear');
    setValue('ophthalmoscopy.leftFundusApp', '');
    setValue('ophthalmoscopy.leftMedia', 'Clear');
    setValue('ophthalmoscopy.leftAdditionalNotes', '');
    setValue('ophthalmoscopy.rightLens', 'Clear');
    setValue('ophthalmoscopy.rightVitreous', 'Clear');
    setValue('ophthalmoscopy.rightDiscColor', 'Pink');
    setValue('ophthalmoscopy.rightCDRatio', '2');
    setValue('ophthalmoscopy.rightDiscMargins', 'Healthy');
    setValue('ophthalmoscopy.rightAVRatio', '2/3');
    setValue('ophthalmoscopy.rightMacula', 'Clear');
    setValue('ophthalmoscopy.rightPeriphery', 'Clear');
    setValue('ophthalmoscopy.rightFundusApp', '');
    setValue('ophthalmoscopy.rightMedia', 'Clear');
    setValue('ophthalmoscopy.rightAdditionalNotes', '');
  };

  return (
    <>
      <Stack
        direction="row"
        alignItems="right"
        justifyContent="right"
        ml={1}
        mr={1}
        spacing={2}
      >
        <Button variant="contained" onClick={clearExternalData}>
          Clear
        </Button>
        <Button variant="contained" onClick={defaultValues}>
          Default
        </Button>
        <Button variant="contained" onClick={copyData}>
          Copy R to L
        </Button>
      </Stack>
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          flexDirection: 'row',
          justifyContent: 'space-between',
        }}
        p={5}
      >
        <Box
          sx={{
            width: '33.33%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Typography color={'#637381'} variant="h6" mb={3}>
            Right
          </Typography>
          <RHFTextField
            name="ophthalmoscopy.leftLens"
            size="small"
            sx={{ width: '90%', marginBottom: '10px' }}
          />
          <RHFTextField
            name="ophthalmoscopy.leftVitreous"
            size="small"
            sx={{ width: '90%', marginBottom: '10px' }}
          />
          <RHFTextField
            name="ophthalmoscopy.leftDiscColor"
            size="small"
            sx={{ width: '90%', marginBottom: '10px' }}
          />
          <RHFTextField
            name="ophthalmoscopy.leftCDRatio"
            size="small"
            sx={{ width: '90%', marginBottom: '10px' }}
          />
          <RHFTextField
            name="ophthalmoscopy.leftDiscMargins"
            size="small"
            sx={{ width: '90%', marginBottom: '10px' }}
          />
          <RHFTextField
            name="ophthalmoscopy.leftAVRatio"
            size="small"
            sx={{ width: '90%', marginBottom: '10px' }}
          />
          <RHFTextField
            name="ophthalmoscopy.leftMacula"
            size="small"
            sx={{ width: '90%', marginBottom: '10px' }}
          />
          <RHFTextField
            name="ophthalmoscopy.leftPeriphery"
            size="small"
            sx={{ width: '90%', marginBottom: '10px' }}
          />
          <RHFTextField
            name="ophthalmoscopy.leftFundusApp"
            size="small"
            sx={{ width: '90%', marginBottom: '10px' }}
          />
          <RHFTextField
            name="ophthalmoscopy.leftMedia"
            size="small"
            sx={{ width: '90%', marginBottom: '10px' }}
          />
          <RHFTextField
            name="ophthalmoscopy.leftAdditionalNotes"
            size="small"
            multiline
            rows={4}
            sx={{ width: '90%', marginBottom: '10px' }}
          />
          <Badge
            color="success"
            variant="dot"
            invisible={!hasLeftFundusImage}
            sx={{
              width: '90%',
              mb: '10px',
              '& .MuiBadge-badge': { top: 8, right: 16 },
            }}
          >
            <Button
              variant="outlined"
              onClick={() => handleCanvasModal('left')}
              sx={{ width: '100%' }}
            >
              Images
            </Button>
          </Badge>
        </Box>
        <Box
          sx={{
            width: '33.33%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Typography color={'#637381'} variant="h6" mt={2}></Typography>
          <Typography color={'#637381'} variant="h6" mt={3}>
            Lens
          </Typography>
          <Typography color={'#637381'} variant="h6" mt={3}>
            Vitreous
          </Typography>
          <Typography color={'#637381'} variant="h6" mt={3}>
            Disc Color
          </Typography>
          <Typography color={'#637381'} variant="h6" mt={3}>
            C:D Ratio
          </Typography>
          <Typography color={'#637381'} variant="h6" mt={3}>
            Disc Margins
          </Typography>
          <Typography color={'#637381'} variant="h6" mt={3}>
            A/V Ratio
          </Typography>
          <Typography color={'#637381'} variant="h6" mt={3}>
            Macula
          </Typography>
          <Typography color={'#637381'} variant="h6" mt={3}>
            Periphery
          </Typography>
          <Typography color={'#637381'} variant="h6" mt={3}>
            Fundus App
          </Typography>
          <Typography color={'#637381'} variant="h6" mt={3}>
            Media
          </Typography>
          <Typography color={'#637381'} variant="h6" mb={9} mt={3}>
            Additional Notes
          </Typography>
        </Box>
        <Box
          sx={{
            width: '33.33%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Typography color={'#637381'} variant="h6" mb={3}>
            Left
          </Typography>
          <RHFTextField
            name="ophthalmoscopy.rightLens"
            size="small"
            sx={{ width: '90%', marginBottom: '10px' }}
          />
          <RHFTextField
            name="ophthalmoscopy.rightVitreous"
            size="small"
            sx={{ width: '90%', marginBottom: '10px' }}
          />
          <RHFTextField
            name="ophthalmoscopy.rightDiscColor"
            size="small"
            sx={{ width: '90%', marginBottom: '10px' }}
          />
          <RHFTextField
            name="ophthalmoscopy.rightCDRatio"
            size="small"
            sx={{ width: '90%', marginBottom: '10px' }}
          />
          <RHFTextField
            name="ophthalmoscopy.rightDiscMargins"
            size="small"
            sx={{ width: '90%', marginBottom: '10px' }}
          />
          <RHFTextField
            name="ophthalmoscopy.rightAVRatio"
            size="small"
            sx={{ width: '90%', marginBottom: '10px' }}
          />
          <RHFTextField
            name="ophthalmoscopy.rightMacula"
            size="small"
            sx={{ width: '90%', marginBottom: '10px' }}
          />
          <RHFTextField
            name="ophthalmoscopy.rightPeriphery"
            size="small"
            sx={{ width: '90%', marginBottom: '10px' }}
          />
          <RHFTextField
            name="ophthalmoscopy.rightFundusApp"
            size="small"
            sx={{ width: '90%', marginBottom: '10px' }}
          />
          <RHFTextField
            name="ophthalmoscopy.rightMedia"
            size="small"
            sx={{ width: '90%', marginBottom: '10px' }}
          />
          <RHFTextField
            name="ophthalmoscopy.rightAdditionalNotes"
            size="small"
            multiline
            rows={4}
            sx={{ width: '90%', marginBottom: '10px' }}
          />

          <Badge
            color="success"
            variant="dot"
            invisible={!hasRightFundusImage}
            sx={{
              width: '90%',
              mb: '10px',
              '& .MuiBadge-badge': { top: 8, right: 16 },
            }}
          >
            <Button
              variant="outlined"
              onClick={() => handleCanvasModal('right')}
              sx={{ width: '100%' }}
            >
              Images
            </Button>
          </Badge>
        </Box>
      </Box>

      {openCanvasSide !== '' && (
        <Dialog
          fullWidth
          maxWidth="md"
          open={Boolean(openCanvasSide)}
          onClose={handleCloseCanvasModal}
        >
          <DialogTitle>Examination image</DialogTitle>
          <DialogContent dividers>
            <AddCanvas
              canvas={
                openCanvasSide === 'left'
                  ? leftVisualImage
                  : openCanvasSide === 'right'
                    ? rightVisualImage
                    : undefined
              }
              side={openCanvasSide}
              setCanvas={(value: string) =>
                openCanvasSide === 'left'
                  ? setLeftCanvas(value)
                  : openCanvasSide === 'right'
                    ? setRightCanvas(value)
                    : null
              }
              type="ophthalmoscopy"
            />
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={handleCloseCanvasModal}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default Ophthalmoscopy;
